import {
  DEFAULT_PRIORITY_COLORS,
  DEFAULT_TASK_SWIPE_ACTIONS,
  type ExportData,
  SCHEMA_VERSION,
} from '@portfolio-pm/shared';

interface RtmList {
  id: string;
  name: string;
  date_created: number;
  date_modified: number;
  archived?: boolean | number | string | null;
  is_archived?: boolean | number | string | null;
  date_archived?: number | null;
  archived_at?: number | null;
}

interface RtmSmartList {
  id: string;
  name: string;
  filter?: string | null;
  sorting_scheme_id?: string | null;
  relative_position?: number | null;
  date_created: number;
  date_modified: number;
}

interface RtmNote {
  series_id: string;
  date_created?: number;
  date_modified?: number;
  title?: string;
  content?: string;
}

interface RtmTask {
  id: string;
  series_id: string;
  list_id: string;
  name: string;
  priority?: string;
  date_created: number;
  date_added?: number;
  date_modified: number;
  date_completed?: number | null;
  date_due?: number | null;
  date_due_has_time?: boolean;
  date_start?: number | null;
  date_start_has_time?: boolean;
  date_trashed?: number | null;
  estimate?: string | null;
  source?: string;
  repeat?: string | null;
  repeat_every?: boolean;
  tags?: string[];
  url?: string | null;
  location_id?: string | null;
  parent_id?: string | null;
  postponed?: number;
}

interface RtmLocation {
  id: string;
  name: string;
}

interface RtmTag {
  id: string;
  foreground_color?: string | null;
  background_color?: string | null;
  date_created?: number;
  date_modified?: number;
}

interface RtmFavorite {
  favorite_ctx_id: string;
  favorite_ctx_type?: string;
  relative_position?: number | null;
  date_created?: number;
  date_modified?: number;
}

interface RtmSortingProperty {
  field?: string;
  ascending?: boolean;
}

interface RtmSortingScheme {
  id: string;
  group_by?: string | null;
  properties?: RtmSortingProperty[];
}

interface RtmExport {
  lists: RtmList[];
  tasks: RtmTask[];
  smart_lists?: RtmSmartList[];
  tags?: RtmTag[];
  notes?: RtmNote[];
  locations?: RtmLocation[];
  favorites?: RtmFavorite[];
  sorting_schemes?: RtmSortingScheme[];
}

export interface RtmImportResult {
  data: ExportData;
  projectCount: number;
  taskCount: number;
}

export function convertRtmExportToPpm(raw: unknown, includeCompleted = false): RtmImportResult {
  const rtm = parseRtmExport(raw);
  const importedAt = Date.now();
  const locationNameById = new Map<string, string>();
  for (const location of rtm.locations ?? []) {
    locationNameById.set(location.id, location.name);
  }

  const favorites = [...(rtm.favorites ?? [])].sort(compareRtmPosition);
  const favoriteTaskIds = new Set<string>();
  for (const favorite of favorites) {
    const type = favorite.favorite_ctx_type?.toLowerCase() ?? '';
    if (type && !type.includes('task')) continue;
    favoriteTaskIds.add(String(favorite.favorite_ctx_id));
  }

  const notesBySeries = new Map<string, string>();
  for (const note of rtm.notes ?? []) {
    const parts = [note.title?.trim(), note.content?.trim()].filter(Boolean);
    if (parts.length === 0) continue;
    const text = parts.join('\n\n');
    const existing = notesBySeries.get(note.series_id);
    notesBySeries.set(note.series_id, existing ? `${existing}\n\n---\n\n${text}` : text);
  }

  const inboxRtmId = rtm.lists.find((list) => list.name === 'Inbox')?.id;
  const rtmListNameToId = new Map(rtm.lists.map((list) => [list.name.toLowerCase(), list.id]));
  const rtmListIds = new Set(rtm.lists.map((list) => list.id));
  const rtmSmartListIds = new Set((rtm.smart_lists ?? []).map((list) => list.id));
  const rtmSortingSchemes = new Map(
    (rtm.sorting_schemes ?? []).map((scheme) => [scheme.id, scheme]),
  );
  const listIdToPpmId = (rtmListId: string): string =>
    rtmListId === inboxRtmId ? 'p_inbox' : `rtm_list_${rtmListId}`;
  const smartListIdToPpmId = (rtmSmartListId: string): string => `rtm_smart_${rtmSmartListId}`;
  const tagSmartListId = (tag: string): string => `rtm_tag_${encodeURIComponent(tag)}`;

  const projects: ExportData['projects'] = rtm.lists.map((list, index) => {
    const isInbox = list.id === inboxRtmId;
    return {
      id: isInbox ? 'p_inbox' : `rtm_list_${list.id}`,
      name: list.name,
      inbox: isInbox ? true : undefined,
      archived: isInbox ? false : isRtmListArchived(list),
      order: index,
      createdAt: list.date_created,
      updatedAt: latestTimestamp(list.date_modified, list.date_archived, list.archived_at),
    };
  });

  const smartLists: NonNullable<ExportData['smartLists']> = (rtm.smart_lists ?? []).map(
    (smartList, index) => {
      const sorting = smartList.sorting_scheme_id
        ? rtmSortingSchemes.get(smartList.sorting_scheme_id)
        : undefined;
      return {
        id: smartListIdToPpmId(smartList.id),
        name: smartList.name,
        query: normalizeRtmSmartListFilter(smartList.filter, rtmListNameToId, listIdToPpmId),
        groupBy: mapRtmGroupBy(sorting?.group_by),
        groupDirection: 'asc',
        sort: mapRtmSort(sorting),
        showCompleted: true,
        showTrashed: false,
        order: smartList.relative_position ?? index,
        createdAt: smartList.date_created,
        updatedAt: smartList.date_modified,
      };
    },
  );

  const smartListIds = new Set(smartLists.map((smartList) => smartList.id));
  for (const favorite of favorites) {
    const type = favorite.favorite_ctx_type?.toLowerCase() ?? '';
    if (type !== 'tag') continue;
    const tag = String(favorite.favorite_ctx_id);
    const id = tagSmartListId(tag);
    if (smartListIds.has(id)) continue;
    smartListIds.add(id);
    smartLists.push({
      id,
      name: `#${tag}`,
      query: `tag:"${escapeQueryValue(tag)}"`,
      groupBy: 'project',
      groupDirection: 'asc',
      sort: [{ key: 'due', direction: 'asc' }],
      showCompleted: true,
      showTrashed: false,
      order: favorite.relative_position ?? smartLists.length,
      createdAt: favorite.date_created ?? 0,
      updatedAt: favorite.date_modified ?? favorite.date_created ?? 0,
    });
  }

  const tagColors: Record<string, string> = {};
  for (const tag of rtm.tags ?? []) {
    const color =
      normalizeHexColor(tag.background_color) ?? normalizeHexColor(tag.foreground_color);
    if (color) tagColors[tag.id] = color;
  }

  const favoriteShortcuts: NonNullable<ExportData['uiPreferences']>['favoriteShortcuts'] = [];
  const seenFavoriteShortcuts = new Set<string>();
  for (const favorite of favorites) {
    const type = favorite.favorite_ctx_type?.toLowerCase() ?? '';
    const rtmId = String(favorite.favorite_ctx_id);
    const shortcut =
      type === 'tag'
        ? { kind: 'smart' as const, id: tagSmartListId(rtmId) }
        : type === 'list' && rtmSmartListIds.has(rtmId)
          ? { kind: 'smart' as const, id: smartListIdToPpmId(rtmId) }
          : type === 'list' && rtmListIds.has(rtmId)
            ? { kind: 'project' as const, id: listIdToPpmId(rtmId) }
            : undefined;
    if (!shortcut) continue;
    const key = `${shortcut.kind}:${shortcut.id}`;
    if (seenFavoriteShortcuts.has(key)) continue;
    seenFavoriteShortcuts.add(key);
    favoriteShortcuts.push(shortcut);
  }

  const tasks: ExportData['tasks'] = [];
  for (const rtmTask of rtm.tasks) {
    if (!includeCompleted && rtmTask.date_completed) continue;

    const task: ExportData['tasks'][number] = {
      id: `rtm_task_${rtmTask.id}`,
      title: rtmTask.name,
      projectId: listIdToPpmId(rtmTask.list_id),
      workflowState: 'backlog',
      priority: mapPriority(rtmTask.priority),
      tags: rtmTask.tags ?? [],
      createdAt: rtmTask.date_created,
      updatedAt: rtmTask.date_modified,
    };

    const notes = notesBySeries.get(rtmTask.series_id);
    if (notes) task.notes = notes;
    if (rtmTask.parent_id) task.parentTaskId = `rtm_task_${rtmTask.parent_id}`;
    if (rtmTask.date_due) {
      task.dueAt = rtmTask.date_due;
      task.dueAtHasTime = rtmTask.date_due_has_time ?? false;
    }
    if (rtmTask.date_start) {
      task.scheduledAt = rtmTask.date_start;
      task.scheduledAtHasTime = rtmTask.date_start_has_time ?? false;
    }
    if (rtmTask.date_completed) task.completedAt = rtmTask.date_completed;
    if (rtmTask.date_trashed) task.trashedAt = rtmTask.date_trashed;
    if (rtmTask.url) task.url = rtmTask.url;
    if (rtmTask.postponed !== undefined) task.postponed = rtmTask.postponed;
    if (rtmTask.source) task.source = rtmTask.source;
    if (favoriteTaskIds.has(rtmTask.id) || favoriteTaskIds.has(rtmTask.series_id)) {
      task.favorite = true;
    }
    if (rtmTask.location_id) {
      const locationName = locationNameById.get(rtmTask.location_id);
      if (locationName) task.location = locationName;
    }

    const estimateMinutes = parseEstimateMinutes(rtmTask.estimate);
    if (estimateMinutes) task.estimateMinutes = estimateMinutes;
    if (rtmTask.repeat) {
      task.recurrence = {
        seriesId: `rtm_series_${rtmTask.series_id}`,
        rrule: rtmTask.repeat,
        fromCompletion: rtmTask.repeat_every === true,
      };
    }

    tasks.push(task);
  }

  const data: ExportData = {
    schemaVersion: SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    projects,
    objectives: [],
    keyResults: [],
    checkIns: [],
    uiPreferences:
      Object.keys(tagColors).length > 0 || favoriteShortcuts.length > 0
        ? {
            priorityColors: DEFAULT_PRIORITY_COLORS,
            tagColors,
            taskSwipeLeftToRight: DEFAULT_TASK_SWIPE_ACTIONS.leftToRight,
            taskSwipeRightToLeft: DEFAULT_TASK_SWIPE_ACTIONS.rightToLeft,
            favoriteShortcuts,
            projectSidebarSort: 'manual',
            projectSidebarCount: 'active',
            projectSidebarShowArchived: projects.some((project) => project.archived),
            appBadgeCountMode: 'today',
            createdAt:
              latestTimestamp(
                ...[...(rtm.tags ?? []), ...favorites].map((item) => item.date_created),
              ) || importedAt,
            updatedAt:
              latestTimestamp(
                ...[...(rtm.tags ?? []), ...favorites].map((item) => item.date_modified),
              ) || importedAt,
          }
        : undefined,
    smartLists,
    tasks,
  };

  return { data, projectCount: projects.length, taskCount: tasks.length };
}

function parseRtmExport(raw: unknown): RtmExport {
  if (!isRecord(raw)) {
    throw new Error('Remember the Milk export must be a JSON object.');
  }
  if (!Array.isArray(raw.lists) || !Array.isArray(raw.tasks)) {
    throw new Error('Remember the Milk export must include lists and tasks arrays.');
  }
  return raw as unknown as RtmExport;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function mapPriority(rtm: string | undefined): 0 | 1 | 2 | 3 {
  if (rtm === 'P1') return 1;
  if (rtm === 'P2') return 2;
  if (rtm === 'P3') return 3;
  return 0;
}

function isRtmListArchived(list: RtmList): boolean {
  return Boolean(
    truthyArchiveFlag(list.archived) ||
      truthyArchiveFlag(list.is_archived) ||
      list.date_archived ||
      list.archived_at,
  );
}

function truthyArchiveFlag(value: boolean | number | string | null | undefined): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return false;
}

function latestTimestamp(...values: Array<number | null | undefined>): number {
  return Math.max(0, ...values.filter((value): value is number => typeof value === 'number'));
}

function compareRtmPosition(
  left: { relative_position?: number | null; date_created?: number },
  right: { relative_position?: number | null; date_created?: number },
): number {
  return (
    (left.relative_position ?? Number.MAX_SAFE_INTEGER) -
      (right.relative_position ?? Number.MAX_SAFE_INTEGER) ||
    (left.date_created ?? 0) - (right.date_created ?? 0)
  );
}

function normalizeHexColor(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const normalized = value.trim();
  return /^#[0-9a-f]{6}$/i.test(normalized) ? normalized : undefined;
}

function normalizeRtmSmartListFilter(
  filter: string | null | undefined,
  rtmListNameToId: Map<string, string>,
  listIdToPpmId: (rtmListId: string) => string,
): string {
  let query = filter?.trim();
  if (!query) return 'all';
  query = query.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");
  query = query.replace(/\blist:(?:"([^"]+)"|([^\s)]+))/gi, (_match, quoted, bare) => {
    const listName = String(quoted ?? bare ?? '');
    const rtmListId = rtmListNameToId.get(listName.toLowerCase());
    return rtmListId
      ? `list:"${listIdToPpmId(rtmListId)}"`
      : `list:"${escapeQueryValue(listName)}"`;
  });
  query = query.replace(
    /\btimeEstimate:"?([<>]=?|=)?\s*(\d+)\s*(minutes?|mins?|min|m|hours?|hrs?|h)"?/gi,
    (_match, operator, amount, unit) => {
      const minutes =
        Number.parseInt(amount, 10) * (String(unit).toLowerCase().startsWith('h') ? 60 : 1);
      return `timeEstimate:${operator ?? '='}${minutes}`;
    },
  );
  query = query.replace(/\btimeEstimate:none\b/gi, 'notimeestimate:true');
  query = query.replace(/\bisRepeating:(true|false)\b/gi, 'recurrence:$1');
  query = query.replace(/\bstart:never\b/gi, 'scheduled:never');
  return insertImplicitAnd(query);
}

function insertImplicitAnd(query: string): string {
  const tokens = tokenizeQuery(query);
  const out: string[] = [];
  for (const token of tokens) {
    const previous = out[out.length - 1];
    if (previous && canEndQueryAtom(previous) && canStartQueryAtom(token)) out.push('AND');
    out.push(token);
  }
  return out.join(' ');
}

function tokenizeQuery(query: string): string[] {
  const tokens: string[] = [];
  let index = 0;
  while (index < query.length) {
    const char = query[index];
    if (!char || /\s/.test(char)) {
      index++;
      continue;
    }
    if (char === '(' || char === ')') {
      tokens.push(char);
      index++;
      continue;
    }
    let end = index;
    let quoted = false;
    while (end < query.length) {
      const current = query[end];
      if (current === '"') quoted = !quoted;
      if (!quoted && (current === '(' || current === ')' || /\s/.test(current ?? ''))) break;
      end++;
    }
    tokens.push(query.slice(index, end));
    index = end;
  }
  return tokens;
}

function canEndQueryAtom(token: string): boolean {
  const upper = token.toUpperCase();
  return token === ')' || !['(', 'AND', 'OR', 'NOT'].includes(upper);
}

function canStartQueryAtom(token: string): boolean {
  const upper = token.toUpperCase();
  return token === '(' || upper === 'NOT' || ![')', 'AND', 'OR'].includes(upper);
}

function escapeQueryValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function mapRtmGroupBy(
  groupBy: string | null | undefined,
): NonNullable<ExportData['smartLists']>[number]['groupBy'] {
  switch (groupBy) {
    case 'list':
      return 'project';
    case 'tags':
      return 'tag';
    case 'priority':
      return 'priority';
    case 'date_due':
      return 'due';
    case 'estimate':
      return 'estimate';
    default:
      return 'project';
  }
}

function mapRtmSort(
  sorting: RtmSortingScheme | undefined,
): NonNullable<ExportData['smartLists']>[number]['sort'] {
  const sort = (sorting?.properties ?? []).flatMap((property) => {
    const key = mapRtmSortField(property.field);
    return key
      ? [{ key, direction: property.ascending === false ? ('desc' as const) : ('asc' as const) }]
      : [];
  });
  return sort.length > 0 ? sort : [{ key: 'due', direction: 'asc' }];
}

function mapRtmSortField(
  field: string | undefined,
): NonNullable<ExportData['smartLists']>[number]['sort'][number]['key'] | undefined {
  switch (field) {
    case 'name':
      return 'name';
    case 'priority':
      return 'priority';
    case 'date_due':
      return 'due';
    case 'date_added':
      return 'created';
    case 'date_updated':
      return 'updated';
    case 'list':
      return 'project';
    case 'estimate':
      return 'estimate';
    default:
      return undefined;
  }
}

function parseEstimateMinutes(estimate: string | null | undefined): number | undefined {
  if (!estimate) return undefined;

  const iso = estimate.match(/^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/i);
  if (iso) {
    const hours = Number.parseInt(iso[1] ?? '0', 10);
    const minutes = Number.parseInt(iso[2] ?? '0', 10);
    const total = hours * 60 + minutes;
    return total > 0 ? total : undefined;
  }

  const natural = estimate.match(/^(\d+)\s*(h(?:ours?)?|m(?:in(?:utes?)?)?)\s*$/i);
  if (natural) {
    const amount = Number.parseInt(natural[1] ?? '0', 10);
    const unit = natural[2] ?? '';
    return unit.toLowerCase().startsWith('h') ? amount * 60 : amount;
  }

  return undefined;
}
